import { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { UserAtom } from "./../atoms/UserAtom";

export enum ITableName {
  ACTIVE_CONTACTS_CAMPAIGN = "ACTIVE_CONTACTS_CAMPAIGN",
  CAMPAIGNS = "CAMPAIGNS",
  CATEGORIES = "CATEGORIES",
  CLICKS = "CLICKS",
  COMPLAINS = "COMPLAINS",
  CONTACTS_DETAILS_TAGS = "CONTACTS_DETAILS_TAGS",
  CONTACTS = "CONTACTS",
  EMAILS = "EMAILS",
  FORMS = "FORMS",
  LINKS = "LINKS",
  NEW_CAMPAIGN = "NEW_CAMPAIGN",
  NOTES = "NOTES",
  OPENS = "OPENS",
  OVERVIEW = "OVERVIEW",
  RECIPIENTS = "RECIPIENTS",
  SETTINGS_TAGS = "SETTINGS_TAGS",
  UNSUBSCRIBERS = "UNSUBSCRIBERS",
  USERS = "USERS",
  VIDEO_CATEGORIES = "VIDEO_CATEGORIES",
  VIDEO_TABLE = "VIDEO_TABLE",
  WEBFORMS = "WEBFORMS",
}

const useRowsPerPage = (
  tableName: ITableName
): [number, (rowsPerPageFromChildSelect: number) => void] => {
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState<number>(0);
  const user = useRecoilValue(UserAtom);

  useEffect(() => {
    if (!user) return;

    try {
      const cachedRowsPerPage = JSON.parse(
        localStorage.getItem(`@berserker/rows_per_page_${user.email}`)
      );

      if (cachedRowsPerPage) {
        setCurrentRowsPerPage(cachedRowsPerPage[tableName]);
      }
    } catch {
      /**
       * If by reason the user erases the rowsPerPage, the JSON.parse will throw an exception
       * that will kill the application
       */
    }
  }, [user, tableName]);

  const updateRowsPerPageObject = useCallback(
    (rowsPerPageFromChildSelect: number) => {
      if (!user) return;

      try {
        let storedValue = JSON.parse(
          localStorage.getItem(`@berserker/rows_per_page_${user.email}`)
        );

        //If there's a previous value on currentRowsPerPage, I want to pick the object from the
        //localStorage and change the value from the key "tableName" to overwrite it.
        if (typeof storedValue === "object" && storedValue) {
          storedValue[tableName] = rowsPerPageFromChildSelect;
        } else {
          //If I don't have any value on localStorage, I want to set the first key-value in the object.
          storedValue = {
            [tableName]: rowsPerPageFromChildSelect,
          };
        }
        setCurrentRowsPerPage(rowsPerPageFromChildSelect);
        localStorage.setItem(
          `@berserker/rows_per_page_${user.email}`,
          JSON.stringify(storedValue)
        );
      } catch {
        // Handles the exception of JSON.parse
      }
    },
    [user, currentRowsPerPage, tableName]
  );

  return [currentRowsPerPage, updateRowsPerPageObject];
};

export default useRowsPerPage;
